import React, { useEffect, useRef, useMemo, useState } from 'react';
import { GoogleMap, LoadScript, Circle } from '@react-google-maps/api';
import { fetchStones } from '../utils/api';
import StoneDetailModal from './StoneDetailModal.jsx';
import RecordVisitModal from './RecordVisitModal.jsx';

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };
const DEFAULT_ZOOM = 10;
const ZOOM_THRESHOLD = 15;

function dedupeById(items) {
  const seen = new Set();
  const out = [];
  for (const it of items || []) {
    const id = it?.id ?? it?.stoneId ?? it?.uuid;
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      lat: Number(it.lat ?? it.latitude),
      lng: Number(it.lng ?? it.longitude),
      name: it.name || it.title || 'Junior Stone',
      description: it.description || '',
      isPrecise: Boolean(it.isPrecise)
    });
  }
  return out.filter(s => Number.isFinite(s.lat) && Number.isFinite(s.lng));
}

export default function MapView({ showPreciseLocations = true, center: centerProp, isAuthenticated = false }) {

  // ---- HOOKS MUST BE FIRST (no early returns above this line) ----
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [stones, setStones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStone, setSelectedStone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recordVisitStone, setRecordVisitStone] = useState(null);
  const [isRecordVisitModalOpen, setIsRecordVisitModalOpen] = useState(false);
  const markersRef = useRef([]);

  const loadStones = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchStones(showPreciseLocations);
      setStones(dedupeById(data));
    } catch (err) {
      console.error('[MapView] reload error:', err);
      setError('Failed to reload stone locations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stones on mount and when precise-mode flips
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchStones(showPreciseLocations, { signal: ctrl.signal });
        if (!alive) return;
        setStones(dedupeById(data));
      } catch (err) {
        if (!alive || err?.name === 'AbortError') return;
        console.error('[MapView] fetch error:', err);
        setError('Failed to load stone locations');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [showPreciseLocations]);

  // Position map when stones or explicit center change
  useEffect(() => {
    if (!map) return;

    if (centerProp && Number.isFinite(centerProp.lat) && Number.isFinite(centerProp.lng)) {
      map.setCenter(centerProp);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    if (stones.length > 0 && window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      stones.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
        return;
      }
    }

    map.setCenter(DEFAULT_CENTER);
    map.setZoom(DEFAULT_ZOOM);
  }, [map, stones, centerProp]);

  useEffect(() => {
  if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

  markersRef.current.forEach(m => { m.map = null; });
  markersRef.current = [];

  stones.forEach(stone => {
    if (!showMarkerFor(stone, zoom)) return;
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: stone.lat, lng: stone.lng },
      title: stone.name,
    });
    marker.addListener('gmp-click', () => {
      console.log('[Marker click] stone:', stone);
      setSelectedStone(stone);
      setIsModalOpen(true);
    });
    markersRef.current.push(marker);
  });

  return () => {
    markersRef.current.forEach(m => { m.map = null; });
    markersRef.current = [];
  };
}, [map, stones, zoom, isAuthenticated]);

const handleZoomChanged = () => {
    if (map) setZoom(map.getZoom());
  };

  const items = stones || [];

  const showMarkerFor = (stone, z) => stone.isPrecise || z < ZOOM_THRESHOLD;
  const showCircleFor = (stone) => !stone.isPrecise;

  // ---- RENDER (returns are below all hooks) ----
  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '70vh', background: '#f5f5f5', borderRadius: 8
      }}>
        Loading stones...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '70vh', background: '#fee', borderRadius: 8, color: '#c33'
      }}>
        {error}
      </div>
    );
  }

  if (typeof window !== 'undefined' && !window.google) {
    return (
      <div style={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading map…
      </div>
    );
  }

  return (
  <>
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '70vh' }}
      onLoad={setMap}
      onZoomChanged={handleZoomChanged}
      options={{
        gestureHandling: 'greedy',
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        mapId: '15eeae0538f45bb2235defcc'
      }}
    >
      {items.map((stone) => {
        const loc = { lat: stone.lat, lng: stone.lng };
        return (
          <React.Fragment key={stone.id}>
            {showCircleFor(stone) && (
              <Circle
                center={loc}
                radius={250}
                options={{
                  fillColor: '#4285F4',
                  fillOpacity: 0.2,
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.4,
                  strokeWeight: 2,
                  clickable: false
                }}
              />
            )}

          </React.Fragment>
        );
      })}
    </GoogleMap>
    
    {isModalOpen && selectedStone && (
      <StoneDetailModal
        stone={selectedStone}
        isOpen={isModalOpen}
        isAuthenticated={isAuthenticated}  
        onClose={() => { setIsModalOpen(false); setSelectedStone(null); }}
        onRecordVisit={(stone) => {
          setSelectedStone(null);
          setIsModalOpen(false);
          setRecordVisitStone(stone);
          setIsRecordVisitModalOpen(true);
        }}
      />
    )}

    <RecordVisitModal
        stone={recordVisitStone}
        isOpen={isRecordVisitModalOpen}
        onClose={() => {
          setIsRecordVisitModalOpen(false);
          setRecordVisitStone(null);
        }}
        onSuccess={loadStones}
      />

  </>
);
}
